<template>
    <v-container class="px-5" fluid>
        <v-row>
            <v-col>
                <h4>Announcements & Offers</h4>
                <p class="text-caption text-grey mb-0">Send messages, tips, and offers to fleet customers.</p>
            </v-col>
        </v-row>

        <v-row>
            <!-- Create / Edit form -->
            <v-col cols="12" md="5">
                <v-card rounded="lg" elevation="2">
                    <v-card-title class="text-subtitle-1 font-weight-bold pa-4 pb-2">
                        {{ editing ? 'Edit Message' : 'New Message' }}
                    </v-card-title>
                    <v-divider/>
                    <v-card-text class="pa-4">
                        <v-select v-model="form.type" :items="typeOptions" item-title="label" item-value="value"
                                  label="Type" density="compact" variant="outlined" hide-details class="mb-3"/>

                        <v-text-field v-model="form.title" label="Title" density="compact" variant="outlined"
                                      hide-details class="mb-3" maxlength="100" counter/>

                        <v-textarea v-model="form.body" label="Message" density="compact" variant="outlined"
                                    hide-details class="mb-3" rows="4" maxlength="500" counter/>

                        <v-text-field v-model="form.cta_url" label="Button URL (optional)" density="compact"
                                      variant="outlined" hide-details class="mb-3"
                                      placeholder="https://..."/>

                        <v-text-field v-model="form.cta_label" label="Button Label (optional)" density="compact"
                                      variant="outlined" hide-details class="mb-3"
                                      placeholder="Learn More"/>

                        <v-select v-model="form.target_portal" :items="portalOptions" item-title="label" item-value="value"
                                  label="Target Portal" density="compact" variant="outlined" hide-details class="mb-3"/>

                        <v-select v-model="form.target_scope" :items="targetOptions" item-title="label" item-value="value"
                                  label="Target Audience" density="compact" variant="outlined" hide-details class="mb-3"/>

                        <company-select v-if="form.target_scope === 'company'" v-model="form.target_company_id"
                                        :items="companies"
                                        label="Company" density="compact" variant="outlined" hide-details
                                        class="mb-3" clearable/>

                        <v-text-field v-model="form.scheduled_at" label="Schedule (optional)"
                                      density="compact" variant="outlined" hide-details class="mb-3"
                                      type="datetime-local"/>

                        <v-text-field v-model="form.expires_at" label="Expires At (optional)"
                                      density="compact" variant="outlined" hide-details class="mb-3"
                                      type="datetime-local"/>

                        <v-switch v-model="form.send_push" label="Send push notification" color="primary"
                                  density="compact" hide-details class="mb-1"/>
                    </v-card-text>
                    <v-divider/>
                    <v-card-actions class="pa-4">
                        <v-btn v-if="editing" variant="text" @click="cancelEdit">Cancel</v-btn>
                        <v-spacer/>
                        <v-btn color="primary" :loading="saving" :disabled="!form.title || !form.body"
                               @click="save">
                            {{ editing ? 'Save Changes' : 'Publish' }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>

            <!-- Messages table -->
            <v-col cols="12" md="7">
                <v-card rounded="lg" elevation="2">
                    <v-card-title class="d-flex align-center pa-4 pb-2">
                        <span class="text-subtitle-1 font-weight-bold">Published Messages</span>
                        <v-spacer/>
                        <v-btn icon size="small" variant="text" :loading="loading" @click="fetchMessages">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-divider/>
                    <v-data-table :headers="headers" :items="messages" :loading="loading"
                                  item-value="id" density="compact" hover>
                        <template #item.type="{ item }">
                            <v-chip :color="typeColor(item.type)" size="x-small" variant="tonal" label>
                                {{ typeLabel(item.type) }}
                            </v-chip>
                        </template>
                        <template #item.status="{ item }">
                            <v-chip :color="statusColor(item)" size="x-small" variant="tonal">
                                {{ statusLabel(item) }}
                            </v-chip>
                        </template>
                        <template #item.target_scope="{ item }">
                            <span class="text-caption">
                                {{ item.target_scope === 'all' ? 'All customers' : companyName(item.target_company_id) }}
                            </span>
                        </template>
                        <template #item.actions="{ item }">
                            <v-btn v-if="!item.dispatched_at" icon size="x-small" variant="text"
                                   @click="editMessage(item)">
                                <v-icon size="16">mdi-pencil-outline</v-icon>
                                <v-tooltip activator="parent" location="top">Edit</v-tooltip>
                            </v-btn>
                            <v-btn icon size="x-small" variant="text" color="error" @click="deleteMessage(item)">
                                <v-icon size="16">mdi-delete-outline</v-icon>
                                <v-tooltip activator="parent" location="top">Delete</v-tooltip>
                            </v-btn>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {ref, reactive, onMounted} from 'vue';
import {send} from '@/composables/apiClient';
import {useSnackbarStore} from '@/stores/snackBarStore';
import CompanySelect from "@/components/globals/company-select.vue";

const snackbar = useSnackbarStore();
const messages = ref([]);
const companies = ref([]);
const loading = ref(false);
const saving = ref(false);
const editing = ref(null);

const blankForm = () => ({
    type: 'announcement',
    title: '',
    body: '',
    cta_url: '',
    cta_label: '',
    target_portal: 'fleet',
    target_scope: 'all',
    target_company_id: null,
    scheduled_at: '',
    expires_at: '',
    send_push: false,
});

const form = reactive(blankForm());

const typeOptions = [
    {label: 'Announcement', value: 'announcement'},
    {label: 'Offer', value: 'offer'},
    {label: 'Tip', value: 'tip'},
    {label: 'New Feature', value: 'feature'},
];

const targetOptions = [
    {label: 'All Customers', value: 'all'},
    {label: 'Specific Company', value: 'company'},
];

const portalOptions = [
    {label: 'Fleet', value: 'fleet'},
    {label: 'SimpliGPS Go', value: 'go'},
    {label: 'Both Portals', value: 'both'},
];

const headers = [
    {title: 'Type', key: 'type', width: 100},
    {title: 'Title', key: 'title'},
    {title: 'Status', key: 'status', width: 100},
    {title: 'Target', key: 'target_scope', width: 120},
    {title: '', key: 'actions', sortable: false, width: 80},
];

const TYPE_CONFIG = {
    offer: {label: 'Offer', color: '#F59E0B'},
    tip: {label: 'Tip', color: '#3B82F6'},
    feature: {label: 'New Feature', color: '#7C3AED'},
    announcement: {label: 'Announcement', color: '#0D9488'},
};

const typeColor = (type) => TYPE_CONFIG[type]?.color ?? 'grey';
const typeLabel = (type) => TYPE_CONFIG[type]?.label ?? type;
const companyName = (id) => companies.value.find(c => c.id === id)?.company_name ?? `Company ${id}`;

const statusLabel = (item) => {
    if (item.deleted_at) return 'Deleted';
    const now = new Date();
    if (item.expires_at && new Date(item.expires_at) < now) return 'Expired';
    if (item.scheduled_at && new Date(item.scheduled_at) > now) return 'Scheduled';
    return 'Active';
};

const statusColor = (item) => {
    const s = statusLabel(item);
    if (s === 'Active') return 'success';
    if (s === 'Scheduled') return 'info';
    if (s === 'Expired') return 'warning';
    return 'error';
};

async function fetchMessages() {
    loading.value = true;
    try {
        const resp = await send('get', 'admin/app-messages');
        messages.value = resp.data.data ?? [];
    } catch {
        snackbar.showSnackbar('Failed to load messages', 'error');
    } finally {
        loading.value = false;
    }
}

async function save() {
    saving.value = true;
    try {
        const payload = {...form};
        if (!payload.cta_url) delete payload.cta_url;
        if (!payload.cta_label) delete payload.cta_label;
        if (!payload.scheduled_at) delete payload.scheduled_at;
        if (!payload.expires_at) delete payload.expires_at;
        if (payload.target_scope !== 'company') delete payload.target_company_id;

        if (editing.value) {
            await send('put', `admin/app-messages/${editing.value}`, payload);
            snackbar.showSnackbar('Message updated', 'success');
        } else {
            await send('post', 'admin/app-messages', payload);
            snackbar.showSnackbar('Message published', 'success');
        }
        Object.assign(form, blankForm());
        editing.value = null;
        await fetchMessages();
    } catch {
        snackbar.showSnackbar('Failed to save message', 'error');
    } finally {
        saving.value = false;
    }
}

function editMessage(item) {
    editing.value = item.id;
    Object.assign(form, {
        type: item.type,
        title: item.title,
        body: item.body,
        cta_url: item.cta_url ?? '',
        cta_label: item.cta_label ?? '',
        target_portal: item.target_portal ?? 'fleet',
        target_scope: item.target_scope ?? 'all',
        target_company_id: item.target_company_id ?? null,
        scheduled_at: item.scheduled_at ? item.scheduled_at.replace(' ', 'T').slice(0, 16) : '',
        expires_at: item.expires_at ? item.expires_at.replace(' ', 'T').slice(0, 16) : '',
        send_push: !!item.send_push,
    });
}

function cancelEdit() {
    editing.value = null;
    Object.assign(form, blankForm());
}

async function deleteMessage(item) {
    if (!confirm(`Delete "${item.title}"?`)) return;
    try {
        await send('delete', `admin/app-messages/${item.id}`);
        snackbar.showSnackbar('Message deleted', 'success');
        await fetchMessages();
    } catch {
        snackbar.showSnackbar('Failed to delete message', 'error');
    }
}

onMounted(() => {
    fetchMessages();
    send('get', 'companies', {list: true})
        .then(r => { companies.value = r?.data?.data ?? []; })
        .catch(() => {});
});
</script>
